// Lettura dell'export Wyscout "partita per partita" di un giocatore (.xlsx).

const normalize = (value) =>
	String(value || "")
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "")
		.toLowerCase()
		.replace(/\s+/g, " ")
		.trim();

// Intestazione → chiave; per le colonne "X / Y" la seconda chiave è il valore riuscito
// (o il sottoinsieme indicato, es. recuperi nella metà campo avversaria).
const KNOWN_COLUMNS = {
	"minuti giocati": ["minutes"],
	"azioni totali / riuscite": ["actions", "actionsOk"],
	goal: ["goals"],
	assist: ["assists"],
	"tiri / in porta": ["shots", "shotsOnTarget"],
	xg: ["xg"],
	"passaggi / accurati": ["passes", "passesOk"],
	"lanci lunghi / accurati": ["longPasses", "longPassesOk"],
	"cross / accurati": ["crosses", "crossesOk"],
	"dribbling / riusciti": ["dribbles", "dribblesOk"],
	"duelli / vinti": ["duels", "duelsWon"],
	"duelli aerei / vinti": ["aerialDuels", "aerialDuelsWon"],
	"intercetti palla": ["interceptions"],
	"palle perse / propria meta campo": ["losses", "lossesOwnHalf"],
	"palle recuperate / meta campo avv.": ["recoveries", "recoveriesOppHalf"],
	"cartellini gialli": ["yellowCards"],
	"cartellini rossi": ["redCards"],
	"duelli difensivi / vinti": ["defensiveDuels", "defensiveDuelsWon"],
	"duelli palla contesa / vinti": ["looseBallDuels", "looseBallDuelsWon"],
	"tackle scivolati / riusciti": ["slidingTackles", "slidingTacklesOk"],
	spazzate: ["clearances"],
	falli: ["fouls"],
	"passaggi per il tiro": ["shotAssists"],
	"duelli offensivi / vinti": ["offensiveDuels", "offensiveDuelsWon"],
	"tocchi in area di rigore": ["touchesInBox"],
	fuorigioco: ["offsides"],
	allunghi: ["progressiveRuns"],
	"falli subiti": ["foulsSuffered"],
	"passaggi filtranti / accurati": ["throughPasses", "throughPassesOk"],
	xa: ["xa"],
	"second assist": ["secondAssists"],
	"passaggi nella trequarti avversaria / accurati": [
		"finalThirdPasses",
		"finalThirdPassesOk",
	],
	"passaggi verso l'area di rigore / riusciti": [
		"passesToBox",
		"passesToBoxOk",
	],
	"passaggi ricevuti": ["receivedPasses"],
	"passaggi in avanti / accurati": ["forwardPasses", "forwardPassesOk"],
	"retropassaggi / accurati": ["backPasses", "backPassesOk"],
	"goal subiti": ["goalsConceded"],
	xcg: ["xcg"],
	"tiri subiti": ["shotsAgainst"],
	"parate / di riflesso": ["saves", "reflexSaves"],
	uscite: ["exits"],
	"retropassaggi al portiere / accurati": [
		"backPassesToGk",
		"backPassesToGkOk",
	],
	"calci di rinvio": ["goalKicks"],
	"calci di rinvio corti": ["shortGoalKicks"],
	"calci di rinvio lunghi": ["longGoalKicks"],
};

// Metriche note dell'export, con l'etichetta che compare nella tab Statistiche.
export const METRIC_OPTIONS = Object.entries(KNOWN_COLUMNS).flatMap(
	([header, keys]) => {
		const [total, part] = header.split(" / ");
		const label = (text) => text.charAt(0).toUpperCase() + text.slice(1);
		return keys.map((key, i) => ({
			value: key,
			label: label(i ? total + " " + part : total),
		}));
	},
);

const INFO_COLUMNS = {
	partita: "match",
	competition: "competition",
	competizione: "competition",
	date: "date",
	data: "date",
	posizione: "position",
	position: "position",
};

const slug = (text) =>
	normalize(text)
		.replace(/[^a-z0-9]+/g, " ")
		.trim()
		.split(" ")
		.map((w, i) => (i ? w.charAt(0).toUpperCase() + w.slice(1) : w))
		.join("");

// Posizioni Wyscout → codici dei ruoli interni.
const POSITION_CODES = {
	GK: "POR",
	RB: "TD",
	RB5: "QD",
	RWB: "QD",
	LB: "TS",
	LB5: "QS",
	LWB: "QS",
	CB: "DC",
	RCB: "DC",
	LCB: "DC",
	DMF: "MED",
	RCMF: "MEZ",
	LCMF: "MEZ",
	AMF: "TRQ",
	RW: "ED",
	RWF: "ED",
	LW: "ES",
	LWF: "ES",
	CF: "PC",
	SS: "SP",
};

// Il suffisso "3" indica la stessa posizione nella difesa a tre (es. LCB3).
export const mapWyscoutPosition = (code) => {
	const clean = String(code || "")
		.trim()
		.toUpperCase();
	return (
		POSITION_CODES[clean] || POSITION_CODES[clean.replace(/3$/, "")] || null
	);
};

// "Italy. Serie A" → "Serie A"
export const cleanCompetition = (name) =>
	String(name || "")
		.replace(/^[^.]+\.\s+/, "")
		.trim();

const FRIENDLY = /friendl|amichevol/i;

export const isOfficialCompetition = (name) => !FRIENDLY.test(name || "");

// La stagione cambia a luglio: 2026-08-17 → 2026/27, 2026-05-04 → 2025/26.
export const seasonFromDate = (date) => {
	const match = /^(\d{4})-(\d{2})/.exec(String(date || ""));
	if (!match) return null;
	const year = Number(match[1]);
	const start = Number(match[2]) >= 7 ? year : year - 1;
	return start + "/" + String(start + 1).slice(-2);
};

const toNumber = (value) => {
	if (value === null || value === undefined || value === "") return 0;
	const n =
		typeof value === "number"
			? value
			: parseFloat(String(value).replace(",", "."));
	return Number.isFinite(n) ? n : 0;
};

const toIsoDate = (value) => {
	if (typeof value === "number") {
		// Data seriale di Excel
		const ms = Math.round((value - 25569) * 86400000);
		return new Date(ms).toISOString().slice(0, 10);
	}
	const text = String(value || "").trim();
	const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
	if (iso) return iso[0];
	const it = /^(\d{1,2})[/.](\d{1,2})[/.](\d{4})/.exec(text);
	return it
		? `${it[3]}-${it[2].padStart(2, "0")}-${it[1].padStart(2, "0")}`
		: null;
};

const parseMatchName = (name) => {
	const m = /^(.*?)\s+-\s+(.*?)\s+(\d+):(\d+)\s*$/.exec(String(name || ""));
	return m
		? {home: m[1], away: m[2], homeGoals: Number(m[3]), awayGoals: Number(m[4])}
		: {home: String(name || ""), away: "", homeGoals: null, awayGoals: null};
};

// rows: righe del foglio con intestazione (sheet_to_json con header: 1).
export const parseWyscoutRows = (rows) => {
	const [header = [], ...data] = rows || [];
	const columns = [];
	const metrics = [];
	const seen = new Set();
	for (let i = 0; i < header.length; i++) {
		const label = String(header[i] || "").trim();
		if (!label) continue;
		const key = normalize(label);
		if (INFO_COLUMNS[key]) {
			columns.push({index: i, info: INFO_COLUMNS[key]});
			continue;
		}
		const paired = label.includes("/") && !String(header[i + 1] || "").trim();
		const [totalLabel, partLabel] = label.split("/").map((s) => s.trim());
		const keys =
			KNOWN_COLUMNS[key] ||
			(paired ? [slug(totalLabel), slug(totalLabel) + "Ok"] : [slug(label)]);
		// I cartellini compaiono due volte: si tiene la prima colonna.
		if (seen.has(keys[0])) {
			if (paired) i++;
			continue;
		}
		seen.add(keys[0]);
		columns.push({index: i, key: keys[0]});
		metrics.push({key: keys[0], label: totalLabel});
		if (paired && keys[1]) {
			columns.push({index: i + 1, key: keys[1]});
			metrics.push({
				key: keys[1],
				label: totalLabel + " " + partLabel,
				of: keys[0],
			});
			i++;
		}
	}

	const unmapped = new Set();
	const matches = data
		.filter((row) => row && row.some((v) => v !== null && v !== ""))
		.map((row) => {
			const match = {values: {}};
			columns.forEach((c) => {
				if (c.info) match[c.info] = row[c.index];
				else match.values[c.key] = toNumber(row[c.index]);
			});
			const positions = String(match.position || "")
				.split(",")
				.map((p) => p.trim())
				.filter((p) => p && p !== "0");
			positions.forEach((p) => !mapWyscoutPosition(p) && unmapped.add(p));
			const competitionRaw = String(match.competition || "");
			const date = toIsoDate(match.date);
			return {
				...parseMatchName(match.match),
				name: String(match.match || ""),
				date,
				season: seasonFromDate(date),
				competition: cleanCompetition(competitionRaw),
				official: isOfficialCompetition(competitionRaw),
				positions,
				roles: [...new Set(positions.map(mapWyscoutPosition).filter(Boolean))],
				values: match.values,
			};
		})
		.filter((m) => m.date)
		.sort((a, b) => b.date.localeCompare(a.date));

	return {metrics, matches, unmappedPositions: [...unmapped].sort()};
};

// "Player stats F. Baschirotto.xlsx" → "F. Baschirotto"
export const playerNameFromFile = (fileName) =>
	String(fileName || "")
		.replace(/\.[^.]+$/, "")
		.replace(/^player stats\s*/i, "")
		.trim();

export const fileMatchesPlayer = (fileName, player) => {
	const name = normalize(playerNameFromFile(fileName));
	const lastName = normalize(player?.lastName);
	if (!name || !lastName) return true;
	return lastName.split(" ").every((part) => name.includes(part));
};

// ── Aggregazioni ───────────────────────────────────────────────────────────

export const sumMatches = (matches) => {
	const totals = {matches: 0};
	(matches || []).forEach((m) => {
		totals.matches += 1;
		Object.entries(m.values || {}).forEach(([k, v]) => {
			totals[k] = (totals[k] || 0) + v;
		});
	});
	totals.minutes = totals.minutes || 0;
	return totals;
};

export const per90 = (value, minutes) =>
	minutes > 0 && Number.isFinite(value) ? (value / minutes) * 90 : null;

export const ratio = (part, total) =>
	total > 0 && Number.isFinite(part) ? (part / total) * 100 : null;

export const filterMatches = (
	matches,
	{season, competition, officialOnly} = {},
) =>
	(matches || []).filter(
		(m) =>
			(!season || m.season === season) &&
			(!competition || m.competition === competition) &&
			(!officialOnly || m.official),
	);

// Righe per stagione (più recente prima) con i totali.
export const seasonRows = (matches, options = {}) => {
	const bySeason = new Map();
	filterMatches(matches, {...options, season: null}).forEach((m) => {
		if (!bySeason.has(m.season)) bySeason.set(m.season, []);
		bySeason.get(m.season).push(m);
	});
	return [...bySeason.entries()]
		.sort((a, b) => b[0].localeCompare(a[0]))
		.map(([season, list]) => ({
			season,
			competitions: [...new Set(list.map((m) => m.competition))],
			totals: sumMatches(list),
		}));
};

// Competizioni di una stagione con i minuti giocati.
export const competitionOptions = (matches, season) => {
	const minutes = new Map();
	filterMatches(matches, {season}).forEach((m) =>
		minutes.set(
			m.competition,
			(minutes.get(m.competition) || 0) + (m.values.minutes || 0),
		),
	);
	return [...minutes.entries()]
		.sort((a, b) => b[1] - a[1])
		.map(([competition, mins]) => ({competition, minutes: mins}));
};
